/**
 * @file IndividualHomePage.js
 * @description Component for displaying the individual portal home page with navigable tabs.
 * Handles loading portal pages, tab navigation with responsive design, and container management.
 * Supports mobile-friendly interaction with drag-to-scroll functionality and adaptive layout.
 * Integrates with authentication context to ensure user is logged in before displaying content.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './HomePage.css';
import { useAuthContext } from '../../store/Contexts';
import {
    queryScopedPortalPages,
    readScopedPortalPage,
} from '../../services/portalPageService';
import Spinner from '../ui/spinners/Spinner';
import styles from './HomePage.module.scss';
import FreeformGrid from '../fluid-grid/FreeformGrid';
import { logEvent } from '../../services/loggingService';
import { EventDataTypeEnum, LogEventType } from '../../utils/data/enums';
import { getOrLoadRequestCache } from '../../utils/cache/requestCache';
import { ROUTE_BUILDERS } from '../../constants/routes';
import usePortalTabs from '../../hooks/usePortalTabs';
import usePortalPageLoader from '../../hooks/usePortalPageLoader';
import PortalTabBar from '../shared/PortalTabBar';
import { buildPortalDeepLinkState } from '../../utils/portalPageLinks';

const PORTAL_PAGE_LIST_CACHE_TTL_MS = 60 * 1000;
const PORTAL_PAGE_DETAIL_CACHE_TTL_MS = 60 * 1000;

function IndividualHomePage({ keepCurrentUrl = false }) {
    const { isLoggedIn } = useAuthContext();
    const [tenantId, setTenantId] = useState(
        window.localStorage.getItem('tenantId'),
    );

    useEffect(() => {
        const handleStorageChange = () => {
            setTenantId(window.localStorage.getItem('tenantId'));
        };
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const fetchPortalPagesFn = useCallback(async () => {
        return await getOrLoadRequestCache({
            namespace: 'portal-pages-list',
            key: `${tenantId}`,
            ttlMs: PORTAL_PAGE_LIST_CACHE_TTL_MS,
            loader: async () => {
                return await queryScopedPortalPages();
            },
        });
    }, [tenantId]);

    const handleTabClick = tab => {
        logEvent(
            LogEventType.NavigatedToPage,
            `Visited portal page ${tab.name}`,
            {
                subjectDataTypeId: EventDataTypeEnum.Portal,
                subjectId: tab.id,
                subjectName: tab.name,
            },
        );
    };

    const location = useLocation();
    const navigate = useNavigate();

    // A portal page that isn't a main tab is a child page, which opens on its own without the tab bar.
    const openChildPortalPage = useCallback(
        ({ tabFromUrl, selectedFromUrl, hasNavigationStack }) => {
            const childPortalState = buildPortalDeepLinkState(
                location.pathname,
            );
            const isChildPortalPage =
                Boolean(childPortalState) &&
                !Number.isNaN(tabFromUrl) &&
                !selectedFromUrl &&
                !hasNavigationStack;

            if (isChildPortalPage) {
                navigate(
                    {
                        pathname: location.pathname,
                        search: location.search,
                        hash: location.hash,
                    },
                    {
                        replace: true,
                        state: { ...location.state, ...childPortalState },
                    },
                );
            }

            return isChildPortalPage;
        },
        [location, navigate],
    );

    const {
        isLoading,
        setIsLoading,
        selectedTabId,
        portalId,
        visibleTabs,
        showLeftArrow,
        showRightArrow,
        scroll,
        openTab,
        fetchTabs,
        handleMouseDown,
        handleMouseLeave,
        handleMouseUp,
        handleMouseMove,
    } = usePortalTabs({
        fetchPortalPagesFn,
        routeBuilder: ROUTE_BUILDERS.portalPagesView,
        onTabClick: handleTabClick,
        isChildPortalUrl: openChildPortalPage,
        keepCurrentUrl,
    });

    const loadFn = useCallback(
        async id => {
            return await getOrLoadRequestCache({
                namespace: 'portal-page-detail',
                key: `${tenantId}:${id}`,
                ttlMs: PORTAL_PAGE_DETAIL_CACHE_TTL_MS,
                loader: async () => {
                    return await readScopedPortalPage(id);
                },
            });
        },
        [tenantId],
    );

    const {
        fetchedContainers,
        fetchedMobileConfiguration,
        portalName,
        isPageLoading,
        loadPortalPage,
    } = usePortalPageLoader(loadFn);

    useEffect(() => {
        fetchTabs();
    }, [tenantId]);

    useEffect(() => {
        if (selectedTabId !== null) {
            setIsLoading(true);
            loadPortalPage(selectedTabId).finally(() => setIsLoading(false));
        }
    }, [selectedTabId]);

    const isTabLoading = isPageLoading;

    return (
        isLoggedIn && (
            <>
                {isLoading ? (
                    <div className="tabLoadingIndicator">
                        <Spinner className={styles.loadingSpinner} />
                    </div>
                ) : (
                    <>
                        <PortalTabBar
                            visibleTabs={visibleTabs}
                            selectedTabId={selectedTabId}
                            showLeftArrow={showLeftArrow}
                            showRightArrow={showRightArrow}
                            scroll={scroll}
                            openTab={openTab}
                            handleMouseDown={handleMouseDown}
                            handleMouseLeave={handleMouseLeave}
                            handleMouseUp={handleMouseUp}
                            handleMouseMove={handleMouseMove}
                        />

                        {isTabLoading ? (
                            <div className="tabLoadingIndicator">
                                <Spinner className={styles.loadingSpinner} />
                            </div>
                        ) : (
                            <FreeformGrid
                                fetchedContainers={fetchedContainers}
                                fetchedMobileConfiguration={
                                    fetchedMobileConfiguration
                                }
                                navigationStack={[portalId]}
                                portalName={portalName}
                                portalId={portalId}
                            />
                        )}
                    </>
                )}
            </>
        )
    );
}
export default IndividualHomePage;
